import React, { useState, useEffect } from "react";
import {
  STAccordion,
  STButton,
  STDrawer,
  STHeroCommon,
  STProgressBar,
  STSwiper,
} from "..";
import styles from "./STAboutUs.module.css";
import clsx from "clsx";
import {
  faqData,
  ourApproachData,
  processSteps,
  researchMissionfeatures,
  specializationItems,
  teamData,
  testimonialsData,
} from "@/const/data";
import { Icons } from "@/const/icons";
import Image from "next/image";
import {
  ourInnovationImg,
  ourLaboratoryImg,
  rightArrow,
} from "@/assets/images";
import { SwiperSlide } from "swiper/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useAboutUs } from "@/hooks";

export const STAboutUs = ({ pageData, slug = "about-us" }) => {
  const [bioMaterialDrawerOpen, setBioMaterialDrawerOpen] = useState(false);
  const router = useRouter();
  const { activeContent } = useAboutUs(pageData, slug);

  const renderMediaIcon = (icon, defaultIcon, width = 72, height = 72, className = "") => {
    const activeSrc = icon || defaultIcon;
    if (!activeSrc) return null;
    if (typeof activeSrc === "string") {
      if (
        activeSrc.startsWith("http") ||
        activeSrc.startsWith("/") ||
        activeSrc.startsWith("data:")
      ) {
        return (
          <img
            src={activeSrc}
            alt=""
            width={width}
            height={height}
            className={className || styles.researchMissionfeatureIcon}
          />
        );
      }
      return <span className={className}>{activeSrc}</span>;
    }
    if (React.isValidElement(activeSrc)) {
      return activeSrc;
    }
    return (
      <Image
        src={activeSrc}
        alt=""
        width={width}
        height={height}
        className={className || styles.researchMissionfeatureIcon}
      />
    );
  };

  return (
    <>
      <STHeroCommon
        title={activeContent.heroTitle}
        description={activeContent.heroDescription}
        bgImage={activeContent.heroCoverPhoto}
        isVideo={activeContent.heroIsVideo}
      />

      {/* RESEARCH MISSION SECTION START  */}
      <section className={clsx(styles.researchMissionSection, "space-xl-py")}>
        <div className="container">
          <div
            className={clsx(
              styles.researchMissionWrapper,
              "d-flex flex-column flex-xl-row gap-lg",
            )}
          >
            <div className={clsx(clsx(styles.researchMissionContent, "w-100"))}>
              {activeContent.sec2Title ? (
                <h2 className="primary-title text-clr-gray mb-0">
                  {activeContent.sec2Title}
                </h2>
              ) : (
                <h2 className="primary-title text-clr-gray mb-0">
                  Advanced bio-materials{" "}
                  <span className="primary-title mb-0">
                    {" "}
                    for transitioning to the circular economy
                  </span>
                </h2>
              )}

              <p className="paragraph-text space-sm-mt space-md-mb">
                {activeContent.sec2Description ||
                  "We believe that transitioning to green technologies should not come with a green-premium. Our bio-coating system is the first amongst many products that we intend to develop, with this same principle."}
              </p>
              {activeContent.sec2CallActions &&
              activeContent.sec2CallActions.length > 0 ? (
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {activeContent.sec2CallActions.map((cta, idx) => (
                    <STButton
                      key={cta.id || idx}
                      suffix={Icons.arrowDoubleRightIcon}
                      variant={
                        cta.type === "Primary" ? "primary" : "outline-primary"
                      }
                      onClick={() => {
                        if (cta.url && cta.url !== "#") {
                          window.location.href = cta.url;
                        } else {
                          setBioMaterialDrawerOpen(true);
                        }
                      }}
                    >
                      {cta.text || "Learn more"}
                    </STButton>
                  ))}
                </div>
              ) : (
                <STButton
                  suffix={Icons.arrowDoubleRightIcon}
                  variant="outline-primary"
                  onClick={() => setBioMaterialDrawerOpen(true)}
                >
                  Learn more
                </STButton>
              )}
            </div>

            <div
              className={clsx(
                styles.researchMissionfeaturesWrap,
                "d-grid gap-sm flex-shrink-0 w-100",
              )}
            >
              {(activeContent.sec2Cards && activeContent.sec2Cards.length > 0
                ? activeContent.sec2Cards
                : researchMissionfeatures
              ).map((item, index) => (
                <div
                  key={index}
                  className={clsx(
                    styles.card,
                    "bg-off-white space-md-p radius-lg",
                  )}
                >
                  {renderMediaIcon(item.icon, researchMissionfeatures[index]?.icon, 72, 72)}
                  <p className={"secondary-title mb-0 h5 space-md-mt"}>
                    {item.description || item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* RESEARCH MISSION SECTION END  */}

      {/* OUR APPROACH SECTION START */}
      <section className={clsx(styles.ourApproachSection, "space-lg-pt")}>
        <div className="container">
          <div
            className={clsx(
              styles.ourApproachheader,
              "d-flex flex-column align-items-center space-lg-mb",
            )}
          >
            {activeContent.sec3Title ? (
              <h2 className="primary-title text-clr-white text-center mb-0">
                {activeContent.sec3Title}
              </h2>
            ) : (
              <>
                <h2 className="primary-title text-clr-white mb-0">
                  Our approach :
                </h2>
                <h2 className="primary-title text-clr-gray-55 mb-0">
                  Science and economy should go hand-in-hand
                </h2>
              </>
            )}
          </div>

          <div
            className={clsx(styles.ourApproachcardsWrapper, "d-grid gap-md")}
          >
            {(activeContent.sec3Cards && activeContent.sec3Cards.length > 0
              ? activeContent.sec3Cards
              : ourApproachData
            ).map((item, index) => (
              <div
                key={index}
                className={clsx(
                  styles.ourApproachcard,
                  "bg-off-white position-relative radius-lg space-md-p cursor-pointer transition-smooth",
                )}
              >
                <span
                  className={clsx(
                    styles.ourApproachIcon,
                    "space-lg-mb d-block transition-smooth",
                  )}
                >
                  {renderMediaIcon(item.icon, ourApproachData[index]?.icon, 32, 32)}
                </span>
                <h3 className="fw-normal mb-2 transition-smooth">
                  {item.subTitle || item.title}
                </h3>
                <p className={clsx("paragraph-text transition-smooth mb-0")}>
                  {item.description || item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* OUR APPROACH SECTION END */}

      {/* OUR INNOVATION SECTION START */}
      {/* <section className="space-xl-py">
        <div className="container">
          <div className="bg-off-white radius-xl space-sm-p">
            <div
              className={clsx(
                styles.ourInnovationHeading,
                "d-flex flex-column flex-sm-row justify-content-between align-items-start gap-sm",
              )}
            >
              <div>
                <h2 className="primary-title mb-0">Our innovation...</h2>
                <h2 className="primary-title text-clr-gray mb-0">
                  What make our laboratory truly stand apart
                </h2>
              </div>
              <div
                className={clsx(
                  styles.reviewBadge,
                  "d-flex align-items-center gap-3 bg-white flex-shrink-0",
                )}
              >
                {Icons.starIcon}
                <p className={clsx("h6 big mb-0")}>27k Reviews</p>
              </div>
            </div>
            <div className={clsx("d-flex gap-sm flex-column flex-lg-row")}>
              <div
                className={clsx(
                  styles.ourInnovationImageWrapper,
                  "w-100 ratio radius-md overflow-hidden",
                )}
              >
                <Image
                  src={ourInnovationImg}
                  alt="our innovation "
                  className="object-fit-cover"
                />
              </div>
              <div
                className={clsx(
                  styles.ourInnovationContent,
                  "w-100 bg-white radius-md space-md-p",
                )}
              >
                <p className="paragraph-text space-md-mb">
                  we stand out through our dedication to excellence, innovation,
                  and scientific integrity. Our lab brings together cutting-edge
                  technology,
                </p>

                <div className="space-lg-mb">
                  <h4 className="secondary-title space-sm-mb">Data accuracy</h4>
                  <STProgressBar value={75} />
                </div>
                <div>
                  <h4 className="secondary-title space-sm-mb">
                    Experimental reliability
                  </h4>
                  <STProgressBar value={85} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
      {/* OUR INNOVATION SECTION END */}

      {/* OUR PROCESS SECTION START */}
      <section className="space-xl-py">
        <div className="container">
          <div className="text-center space-lg-mb">
            {activeContent.sec4Title ? (
              typeof activeContent.sec4Title === "string" &&
              activeContent.sec4Title.includes("Our strength...") ? (
                <>
                  <h2 className="primary-title mb-0">Our strength...</h2>
                  {activeContent.sec4Title.replace("Our strength...", "").trim() && (
                    <h2 className="primary-title text-clr-gray mb-0">
                      {activeContent.sec4Title.replace("Our strength...", "").trim()}
                    </h2>
                  )}
                </>
              ) : typeof activeContent.sec4Title === "string" &&
                activeContent.sec4Title.includes("\n") ? (
                <>
                  <h2 className="primary-title mb-0">
                    {activeContent.sec4Title.split("\n")[0]}
                  </h2>
                  <h2 className="primary-title text-clr-gray mb-0">
                    {activeContent.sec4Title.split("\n").slice(1).join(" ")}
                  </h2>
                </>
              ) : (
                <h2 className="primary-title mb-0">{activeContent.sec4Title}</h2>
              )
            ) : (
              <>
                <h2 className="primary-title mb-0">Our strength...</h2>
                <h2 className="primary-title text-clr-gray mb-0">
                  Materials we develop are closely aligned with the industry from
                  day one
                </h2>
              </>
            )}
            <p
              className={clsx(
                styles.ourProcessDescription,
                "paragraph-text space-sm-mt mx-auto mb-0",
              )}
            >
              {activeContent.sec4Description ||
                "We have all the synthesis, analytical and characterization equipment, demo-plant in-house hence it gives us better control on research and scaling. We collaborate with our partners from industrial trials for accurate results."}
            </p>
          </div>

          <div className={clsx(styles.processSteps, "d-grid")}>
            {(activeContent.sec4Cards && activeContent.sec4Cards.length > 0
              ? activeContent.sec4Cards
              : processSteps
            ).map((step, index, arr) => {
              const defaultBgs = ["18, 178, 98", "45, 152, 218", "255, 178, 26"];
              const bgVal =
                step.bg ||
                processSteps[index]?.bg ||
                defaultBgs[index % defaultBgs.length];
              return (
                <div
                  key={step.id || index}
                  className={clsx(
                    styles.stepWrapper,
                    "d-flex flex-column justify-content-center align-items-center position-relative text-center",
                  )}
                >
                  <div
                    className={clsx(
                      styles.step,
                      "rounded-circle d-flex justify-content-center align-items-center space-md-mb",
                    )}
                    style={{ background: `rgba(${bgVal},0.1)` }}
                  >
                    <div
                      className={styles.icon}
                      style={{ color: `rgb(${bgVal})` }}
                    >
                      {renderMediaIcon(step.icon, processSteps[index]?.icon, 28, 28)}
                    </div>
                  </div>

                  <h3 className={"mb-2 fw-normal h4"}>{step.subTitle || step.title}</h3>
                  <p className={"paragraph-text mb-0"}>{step.description}</p>

                  {/* Arrow */}
                  {index < arr.length - 1 && (
                    <div
                      className={clsx(styles.processArrow, "d-none d-xl-block")}
                    >
                      <Image
                        src={rightArrow}
                        alt="arrow"
                        className="w-100 h-100"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* OUR PROCESS SECTION END */}

      {/* OUR LABORATORY SECTION START */}
      <section className={clsx(styles.ourLaboratorySection, "")}>
        <div className="container">
          <div className="radius-xl bg-off-white space-sm-p">
            <div
              className={clsx(
                styles.ourLaboratoryHeader,
                "d-flex flex-column flex-sm-row justify-content-between align-items-start gap-xs",
              )}
            >
              <div>
                <h2 className="primary-title mb-0">
                  {activeContent.sec5Title || "Our research capabilities..."}
                </h2>
                <h2 className="primary-title text-clr-gray mb-0">
                  {activeContent.sec5Description ||
                    "From lab-scale to demo production, all in-house"}
                </h2>
              </div>
              <STButton
                className={"flex-shrink-0"}
                onClick={() =>
                  router.push(activeContent.sec5ButtonLink || "/contact-us")
                }
              >
                {activeContent.sec5ButtonText || "Let's talk"}
              </STButton>
            </div>
            <div
              className={clsx(
                "d-flex gap-sm flex-column flex-lg-row space-sm-mt",
              )}
            >
              <div
                className={clsx(
                  styles.ourLaboratoryContent,
                  " d-flex flex-column gap-sm",
                )}
              >
                <div className="bg-white space-md-p radius-md">
                  <p className="paragraph-text space-md-mb">
                    {activeContent.sec5InfoDescription ||
                      "From a Scanning Electron Microscope to 1, 5 tons Stainless steel reactors, our strength lies in researching and scaling right at our centre in Chemelot, Netherlands for quick turnaround."}
                  </p>
                  <div className="d-flex gap-4 flex-wrap">
                    {activeContent.sec5Tags &&
                    activeContent.sec5Tags.length > 0 ? (
                      activeContent.sec5Tags.map((tag, idx) => (
                        <div
                          key={tag.id || idx}
                          className={clsx(
                            styles.ourLaboratoryTag,
                            "d-flex align-items-center gap-2 bg-off-white radius-xl",
                          )}
                        >
                          {idx === 0
                            ? Icons.molecularBiologyIcon
                            : idx === 1
                              ? Icons.environmentalIcon
                              : Icons.molecularBiologyIcon}
                          <h3 className="secondary-title mb-0 h5">
                            {tag.name || tag}
                          </h3>
                        </div>
                      ))
                    ) : (
                      <>
                        <div
                          className={clsx(
                            styles.ourLaboratoryTag,
                            "d-flex align-items-center gap-2 bg-off-white radius-xl",
                          )}
                        >
                          {Icons.molecularBiologyIcon}
                          <h3 className="secondary-title mb-0 h5">Chemistry</h3>
                        </div>
                        <div
                          className={clsx(
                            styles.ourLaboratoryTag,
                            "d-flex align-items-center gap-2 bg-off-white radius-xl",
                          )}
                        >
                          {Icons.environmentalIcon}
                          <h3 className="secondary-title mb-0 h5">
                            Biochemistry
                          </h3>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-column flex-sm-row gap-sm h-100">
                  {activeContent.sec5Stats &&
                  activeContent.sec5Stats.length > 0 ? (
                    activeContent.sec5Stats.map((stat, idx) => (
                      <div
                        key={stat.id || idx}
                        className="bg-white radius-md space-md-p w-100"
                      >
                        <h2
                          className={clsx(
                            styles.ourLaboratoryValue,
                            "text-clr-secret-garden mb-0",
                          )}
                        >
                          {stat.number}
                        </h2>
                        <h3 className="secondary-title text-clr-gray mb-0 h5">
                          {stat.label}
                        </h3>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="bg-white radius-md space-md-p w-100">
                        <h2
                          className={clsx(
                            styles.ourLaboratoryValue,
                            "text-clr-secret-garden mb-0",
                          )}
                        >
                          25+
                        </h2>
                        <h3 className="secondary-title text-clr-gray mb-0 h5">
                          Equipment types
                        </h3>
                      </div>
                      <div className="bg-white radius-md space-md-p w-100">
                        <h2
                          className={clsx(
                            styles.ourLaboratoryValue,
                            "text-clr-secret-garden mb-0",
                          )}
                        >
                          ~15
                        </h2>
                        <h3 className="secondary-title text-clr-gray mb-0 h5">
                          tons production capability
                        </h3>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                className={clsx(
                  styles.ourLaboratoryImageWrapper,
                  "w-100 ratio radius-md overflow-hidden",
                )}
              >
                {typeof activeContent.sec5Photo === "string" ? (
                  <img
                    src={activeContent.sec5Photo}
                    alt="our laboratory"
                    className="object-fit-cover w-100 h-100"
                  />
                ) : (
                  <Image
                    src={activeContent.sec5Photo || ourLaboratoryImg}
                    alt="our laboratory"
                    className="object-fit-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* OUR LABORATORY SECTION END */}

      {/* TESTIMONIALS SECTION START */}
      {/* <section className="space-xl-py bg-clr-black">
        <div className="container space-lg-mb">
          <div className="d-flex flex-column flex-sm-row gap-sm justify-content-between align-items-start">
            <div>
              <h2 className="primary-title text-clr-gray-55 mb-0">
                Testimonials that reflect our
              </h2>
              <h2 className="primary-title text-white mb-0">
                scientific commitment
              </h2>
            </div>
            <div className="d-flex align-items-center gap-sm">
              <STButton aria-label="Previous Testimonial" variant="icon" className="testimonial-prev dark">
                {Icons.arrowLeft}
              </STButton>
              <STButton aria-label="Next Testimonial" variant="icon" className="testimonial-next dark">
                {Icons.arrowRight}
              </STButton>
            </div>
          </div>
        </div>
        <STSwiper
          slidesPerView={3.4}
          prevClass=".testimonial-prev"
          nextClass=".testimonial-next"
          className={styles.testimonialSwiper}
          breakpoints={{
            0: { slidesPerView: 1.2, spaceBetween: 20 },
            768: { slidesPerView: 1.8, spaceBetween: 28 },
            992: { slidesPerView: 2.4, spaceBetween: 32 },
            1200: { slidesPerView: 3.2, spaceBetween: 40 },
            1440: { slidesPerView: 3.4 },
          }}
        >
          {testimonialsData.map((item) => (
            <SwiperSlide key={item.id} className={styles.testimonialSlide}>
              <div
                className={clsx(
                  styles.testimonialCard,
                  "bg-off-white radius-md space-md-p h-100 d-flex gap-lg flex-column justify-content-between align-items-start transition-smooth",
                )}
              >
                <p
                  className={clsx(
                    "h6 big fw-normal lh-base mb-0 transition-smooth",
                  )}
                >
                  {item.quote}
                </p>
                <div>
                  <h5 className="mb-2 fw-normal transition-smooth">
                    - {item.author}
                  </h5>
                  <p
                    className={clsx(
                      styles.autherRoal,
                      "mb-0 text-clr-gray transition-smooth",
                    )}
                  >
                    {item.role}
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </STSwiper>
      </section> */}
      {/* TESTIMONIALS SECTION END */}

      {/* OUR TEAM SECTION START */}
      <section className="space-xl-py">
        <div className="container">
          <div
            className={clsx(
              "d-flex justify-content-between align-items-sm-center space-lg-mb gap-xs flex-column flex-sm-row",
            )}
          >
            <div className={clsx(styles.ourTeamTitle)}>
              {activeContent.sec6Title ? (
                <h2 className="primary-title mb-0">{activeContent.sec6Title}</h2>
              ) : (
                <>
                  <h2 className="primary-title mb-0">Our team...</h2>
                  <h2 className="primary-title text-clr-gray mb-0">
                    The brilliant minds powering our research team
                  </h2>
                </>
              )}
            </div>
            <div className="d-flex align-items-center gap-sm">
              <STButton
                aria-label="Previous Team Member"
                variant="icon"
                className="team-prev"
              >
                {Icons.arrowLeft}
              </STButton>
              <STButton
                aria-label="Next Team Member"
                variant="icon"
                className="team-next"
              >
                {Icons.arrowRight}
              </STButton>
            </div>
          </div>

          <STSwiper
            slidesPerView={4}
            spaceBetween={24}
            prevClass=".team-prev"
            nextClass=".team-next"
            breakpoints={{
              0: { slidesPerView: 1 },
              480: { slidesPerView: 1.8 },
              576: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1200: { slidesPerView: 4 },
            }}
          >
            {(activeContent.sec6Members && activeContent.sec6Members.length > 0
              ? activeContent.sec6Members
              : teamData
            ).map((member, index) => (
              <SwiperSlide key={index}>
                <div
                  key={member.id || index}
                  className={clsx(
                    styles.teamCard,
                    "d-flex flex-column align-items-center text-center",
                  )}
                >
                  <div
                    className={clsx(
                      styles.memberImageWrapper,
                      "space-sm-mb rounded-circle overflow-hidden ratio ratio-1x1",
                    )}
                  >
                    {typeof (member.photo || member.image) === "string" ? (
                      <img
                        src={member.photo || member.image}
                        alt={member.name}
                        className="w-100 h-100 object-fit-cover"
                      />
                    ) : (
                      <Image src={member.image} alt={member.name} />
                    )}
                  </div>

                  <h3 className={"h4 mb-2 fw-normal"}>{member.name}</h3>
                  <p className={"h6 big mb-0 fw-normal text-clr-gray"}>
                    {member.designation || member.role}
                  </p>

                  {member.socials ? (
                    <div
                      className={clsx(styles.socials, "space-xs-mt d-flex gap-3")}
                    >
                      {member.socials.map((social, sIdx) => (
                        <Link
                          key={sIdx}
                          href={social.url}
                          target="_blank"
                          className={clsx(
                            styles.socialIcon,
                            styles[social.type],
                            "bg-off-white rounded-circle d-flex justify-content-center align-items-center",
                          )}
                          aria-label={social.type}
                        >
                          {social.icon}
                        </Link>
                      ))}
                    </div>
                  ) : member.linkedin ? (
                    <div
                      className={clsx(styles.socials, "space-xs-mt d-flex gap-3")}
                    >
                      <Link
                        href={member.linkedin}
                        target="_blank"
                        className={clsx(
                          styles.socialIcon,
                          styles.linkedin,
                          "bg-off-white rounded-circle d-flex justify-content-center align-items-center",
                        )}
                        aria-label="linkedin"
                      >
                        {Icons.linkedinIcon || "in"}
                      </Link>
                    </div>
                  ) : null}
                </div>
              </SwiperSlide>
            ))}
          </STSwiper>
        </div>
      </section>
      {/* OUR TEAM SECTION END */}

      {/* OUR SPECIALIZATION STACTION START */}
      <section className={clsx(styles.ourSpecializationSection, "space-xl-py")}>
        <div className="container">
          <div className="text-center space-lg-mb">
            {activeContent.sec7Title ? (
              <h2 className="primary-title text-clr-white mb-0">
                {activeContent.sec7Title}
              </h2>
            ) : (
              <h2 className="primary-title text-clr-white mb-0">
                Our specialization...
              </h2>
            )}
            <h2 className="primary-title text-clr-gray-55 mb-0">
              {activeContent.sec7Description ||
                "Explore our specialized areas of scientific expertise"}
            </h2>
          </div>
          <div
            className={clsx(styles.specializationCardWrapper, "d-grid gap-md")}
          >
            {(activeContent.sec7Cards && activeContent.sec7Cards.length > 0
              ? activeContent.sec7Cards
              : specializationItems
            ).map((item, index) => {
              if (item.type === "image" || item.imagePreview) {
                return (
                  <div
                    key={item.id || index}
                    className={clsx(
                      styles.specializationImgWrapper,
                      "position-relative radius-lg overflow-hidden ratio",
                    )}
                  >
                    {typeof (item.imagePreview || item.image) === "string" ? (
                      <img
                        src={item.imagePreview || item.image}
                        alt={item.alt || ""}
                        className="w-100 h-100 object-fit-cover"
                      />
                    ) : (
                      <Image
                        src={item.image}
                        alt={item.alt}
                        fill
                        className={"object-fit-cover"}
                      />
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={item.id || index}
                  className={clsx(
                    styles.specializationCard,
                    "bg-off-white radius-lg space-md-p",
                  )}
                >
                  <h3 className={"fw-normal space-md-mb h4"}>
                    {item.subTitle || item.title}
                  </h3>
                  <p className={"paragraph-text mb-0"}>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* OUR SPECIALIZATION STACTION END */}

      {/* Bio Material Drawer Start */}
      <STDrawer
        open={bioMaterialDrawerOpen}
        onClose={() => setBioMaterialDrawerOpen(false)}
        title={
          activeContent.sec2DrawerTitle ||
          activeContent.sec2Title ||
          "Advanced bio-materials for transitioning to the circular economy"
        }
      >
        {activeContent.sec2DrawerDescription ? (
          activeContent.sec2DrawerDescription
            .split("\n")
            .map(
              (para, idx) =>
                para.trim() && (
                  <p key={idx} className="paragraph-text h6 space-xs-mb">
                    {para.trim()}
                  </p>
                )
            )
        ) : (
          <>
            <p className="paragraph-text h6 space-xs-mb">
              We believe that transitioning to green technologies should not come
              with a green-premium. Our bio-coating system is the first amongst many
              products that we intend to develop, with this same principle.
            </p>
            <p className="paragraph-text h6 space-xs-mb">
              We are an advanced biomaterials start-up specializing in
              polysaccharide chemistry. The first product we have developed is a
              100% bio-based coating for paper packaging from 2nd generation
              agricultural biomass, providing a high-performance bio-alternative to
              microplastics, synthetic chemicals, and PFAS.
            </p>
            <p className="paragraph-text h6 mb-0">
              Sustanix materials aren't just sustainable—they are efficient.
              Sustainability is the by-product, Performance is the product. We are
              leveraging the complex architecture of nature to solve engineering
              challenges that petroleum cannot, positioning Sustanix as an
              essential partner in the transition to a high-performance circular
              economy.
            </p>
          </>
        )}
      </STDrawer>
      {/* Bio Material Drawer End */}
    </>
  );
};
